import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router';
import * as Sentry from '@sentry/react';
import './app.css';

Sentry.init({
  environment: import.meta.env.VITE_ENVIRONMENT || 'Debug',
  release: import.meta.env.VITE_VERSION || '0.0.0',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [window.location.origin],
      networkResponseHeaders: [
        'x-tenant',
        'x-region',
        'x-trace',
        'x-cache',
        'cf-cache-status',
        'x-amzn-trace-id',
        'cache-control'
      ],
      unblock: ['.sentry-unblock, [data-sentry-unblock]'],
      unmask: ['.sentry-unmask, [data-sentry-unmask]']
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['parcel2go-uiz'],
      behaviour: 'drop-error-if-contains-third-party-frames'
    }),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  beforeSend(event, hint) {
    // Filter out CustomEvent unhandledrejection noise
    const message = event.message || hint.originalException?.toString() || '';
    const exceptionMessage = event.exception?.values?.[0]?.value || '';
    const combinedMessage = `${message} ${exceptionMessage}`;

    if (
      combinedMessage.includes('CustomEvent') &&
      combinedMessage.includes('unhandledrejection') &&
      combinedMessage.includes('captured as promise rejection')
    ) {
      return null; // Don't send this event to Sentry
    }
    return event;
  },
  // Performance Monitoring
  tracesSampleRate: 0.001,
  // Session Replay
  replaysSessionSampleRate: 0.001,
  replaysOnErrorSampleRate: 1.0
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: ':prefix?',
    lazy: async () => {
      const { Component, ErrorBoundary } = await import('~/routes/layouts/root');
      return { Component, ErrorBoundary };
    },
    children: [
      {
        path: 'tracking',
        lazy: async () => {
          const { Component } = await import('~/routes/tracking/search');
          return { Component };
        }
      },
      {
        path: 'tracking/:order',
        lazy: async () => {
          const { Component } = await import('~/routes/tracking/result');
          return { Component };
        }
      }
    ]
  },
  {
    path: '/ui/statics/signin-callback',
    lazy: async () => {
      const { default: Component } = await import('~/routes/signin');
      return { Component };
    }
  },
  {
    path: '/ui/statics/silent-callback',
    lazy: async () => {
      const { default: Component } = await import('~/routes/silent');
      return { Component };
    }
  }
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
